import { toast } from 'react-toastify';
import appApi from '../../../../../api/app-api';
import { displayErrorText } from '../../../../../api/errors/api-error-codes';
import globalUserPage from '../../../../../stores/global-user-page';
import dateUtils from '../../../../../utils/date-format';
import {
  PaymentMethod,
  AllocationWithAmount,
  CheckPaymentDetails,
} from '../payment-method-selection-dialog';
import { bucketFillCheckAllocations } from '../utils/bucket-fill-check-allocations';

// Helper function to calculate the default first payment month/year
const calculateDefaultFirstPaymentMonth = (): {
  month: number;
  year: number;
} => {
  const today = new Date();
  const currentDay = today.getDate();
  const year = today.getFullYear();
  const month = today.getMonth();

  // If today is before the 20th, use current month
  // If today is on or after the 20th, use next month
  if (currentDay < 20) {
    return { month, year };
  } else {
    // Move to next month
    const nextMonth = month + 1;
    if (nextMonth > 11) {
      return { month: 0, year: year + 1 };
    }
    return { month: nextMonth, year };
  }
};

interface SubmitPaymentOperationParams {
  selectedMethod: PaymentMethod | null;
  allocationAmounts: AllocationWithAmount[];
  user: { id: number };
  // Credit Card
  creditCardData: {
    selectedCreditCardTokenId: number | null;
    numberOfInstallments: number;
  };
  // Bank Transfer
  bankTransferData: {
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    transferDate: Date | null;
    referenceId: string;
  };
  // Check
  checkData: {
    checkNumber: string;
    amount: number | null;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    clearingDate: Date | null;
  };
  checks: CheckPaymentDetails[];
  // Scholarship
  scholarshipData: {
    scholarshipId: number | null;
    studentScholarshipId: number | null;
  };
  // Credit Card Installments
  installmentsData: {
    numberOfInstallments: number;
    selectedCreditCardTokenId: number | null;
    firstPaymentMonth: { month: number; year: number } | null;
  };
  // Bank Transfer Installments
  bankTransferInstallmentsData: {
    numberOfInstallments: number;
    bankNumber: string;
    branchNumber: string;
    accountNumber: string;
    firstPaymentMonth: { month: number; year: number } | null;
  };
  // Existing Installments
  selectedExistingInstallmentPlanId: number | null;
  selectedExistingBankTransferPlanId: number | null;
  /** Receipt: «לכבוד» — printed addressee; optional empty omits field */
  recipientName: string;
  /** Receipt: national ID / ת.ז. — sent as backend `identifierId` (required for pay) */
  identifierId: string;
  /** Receipt: phone number — required for pay */
  phoneNumber: string;
  /** Receipt: «עבור» — optional short purpose line */
  issuedFor: string;
  /** Receipt: optional note (trimmed; omitted when empty) */
  note: string;
  setLoading: (loading: boolean) => void;
  onHide: () => void;
}

export const submitPaymentOperation = async (
  params: SubmitPaymentOperationParams,
): Promise<boolean> => {
  const {
    selectedMethod,
    allocationAmounts,
    user,
    creditCardData,
    bankTransferData,
    checkData,
    checks,
    scholarshipData,
    installmentsData,
    bankTransferInstallmentsData,
    selectedExistingInstallmentPlanId,
    selectedExistingBankTransferPlanId,
    recipientName,
    identifierId,
    phoneNumber,
    issuedFor,
    note,
    setLoading,
    onHide,
  } = params;

  if (!selectedMethod || allocationAmounts.length === 0) return false;

  const usesPayReceiptEndpoint =
    selectedMethod === PaymentMethod.CreditCard ||
    selectedMethod === PaymentMethod.BankTransfer ||
    selectedMethod === PaymentMethod.Check ||
    selectedMethod === PaymentMethod.Scholarship ||
    selectedMethod === PaymentMethod.Cash;

  const trimmedIdentifierId = String(identifierId ?? '').trim();
  const trimmedPhoneNumber = String(phoneNumber ?? '').trim();
  if (usesPayReceiptEndpoint) {
    if (!trimmedIdentifierId) {
      toast.warn('יש למלא מספר זהות');
      return false;
    }
    if (!trimmedPhoneNumber) {
      toast.warn('יש למלא מספר טלפון');
      return false;
    }
  }

  const receiptFields = {
    identifierId: trimmedIdentifierId,
    phoneNumber: trimmedPhoneNumber,
    ...(recipientName.trim() && { recipientName: recipientName.trim() }),
    ...(issuedFor.trim() && { issuedFor: issuedFor.trim() }),
    ...(note.trim() && { note: note.trim() }),
  };

  // Validate that all amounts are valid
  const hasInvalidAmount = allocationAmounts.filter(
    (alloc) =>
      !alloc.amount || alloc.amount <= 0 || alloc.amount > alloc.maxAmount,
  );
  if (hasInvalidAmount.length > 0) {
    // Show error message
    toast.warn(
      `שים לב, לא ניתן לשלם עבור ${hasInvalidAmount[0].paymentServiceName} יותר מ ${hasInvalidAmount[0].maxAmount}`,
    );
    console.error('Invalid amounts detected');
    return false;
  }

  try {
    setLoading(true);
    const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
    const totalPaymentAmount = round2(
      allocationAmounts.reduce((sum, alloc) => sum + (alloc.amount || 0), 0),
    );

    // Helper function to calculate the first payment date (20th of the closest month)
    const calculateFirstPaymentDate = (): Date => {
      // If user selected a specific month, use that
      if (installmentsData.firstPaymentMonth) {
        return new Date(
          installmentsData.firstPaymentMonth.year,
          installmentsData.firstPaymentMonth.month,
          20,
        );
      }
      // Otherwise use the default
      const defaultMonth = calculateDefaultFirstPaymentMonth();
      return new Date(defaultMonth.year, defaultMonth.month, 20);
    };

    switch (selectedMethod) {
      case PaymentMethod.CreditCard:
        if (!creditCardData.selectedCreditCardTokenId) {
          toast.warn('יש לבחור כרטיס אשראי');
          setLoading(false);
          return false;
        }
        if (
          !creditCardData.numberOfInstallments ||
          creditCardData.numberOfInstallments < 1 ||
          creditCardData.numberOfInstallments > 10
        ) {
          toast.warn('יש לבחור מספר תשלומים בין 1 ל-10');
          setLoading(false);
          return false;
        }
        const creditCardAllocationRes = await appApi.createPaymentAllocation(
          user.id,
          {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'credit_card',
            paymentDetails: {
              tokenId: creditCardData.selectedCreditCardTokenId,
              numberOfInstallments: creditCardData.numberOfInstallments,
            },
            ...receiptFields,
          },
        );
        if (creditCardAllocationRes.data.errCode) {
          toast.warn(displayErrorText(creditCardAllocationRes.data.errCode));
          setLoading(false);
          return false;
        }
        break;

      case PaymentMethod.BankTransfer:
        if (
          !bankTransferData.bankNumber ||
          !bankTransferData.branchNumber ||
          !bankTransferData.accountNumber ||
          !bankTransferData.transferDate
        ) {
          // Show error message
          setLoading(false);
          return false;
        }
        const trimmedBankTransferReferenceId =
          bankTransferData.referenceId?.trim() ?? '';
        if (trimmedBankTransferReferenceId.length > 30) {
          toast.warn('מספר אסמכתא מוגבל ל-30 תווים');
          setLoading(false);
          return false;
        }
        const bankTransferAllocationRes = await appApi.createPaymentAllocation(
          user.id,
          {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'bank_transfer',
            paymentDetails: {
              bankNumber: bankTransferData.bankNumber,
              branchNumber: bankTransferData.branchNumber,
              accountNumber: bankTransferData.accountNumber,
              transferDate: dateUtils.formattedDateWithNoTime(
                bankTransferData.transferDate,
              ),
              ...(trimmedBankTransferReferenceId !== '' && {
                referenceId: trimmedBankTransferReferenceId,
              }),
            },
            ...receiptFields,
          },
        );
        if (bankTransferAllocationRes.data.errCode) {
          toast.warn(displayErrorText(bankTransferAllocationRes.data.errCode));
          setLoading(false);
          return false;
        }
        break;

      case PaymentMethod.Check: {
        const checksToSend: CheckPaymentDetails[] =
          checks.length > 0
            ? checks
            : (() => {
                const referenceId = checkData.checkNumber?.trim() ?? '';
                const amount =
                  typeof checkData.amount === 'number' ? checkData.amount : NaN;
                if (
                  !referenceId ||
                  !Number.isFinite(amount) ||
                  amount <= 0 ||
                  !checkData.bankNumber ||
                  !checkData.branchNumber ||
                  !checkData.accountNumber
                ) {
                  return [];
                }

                return [
                  {
                    referenceId,
                    amount,
                    bankNumber: checkData.bankNumber,
                    branchNumber: checkData.branchNumber,
                    accountNumber: checkData.accountNumber,
                    dueDate: checkData.clearingDate || undefined,
                    allocations: bucketFillCheckAllocations({
                      allocationTotals: allocationAmounts,
                      checkAmount: amount,
                    }),
                  },
                ];
              })();

        if (checksToSend.length === 0) {
          toast.warn('יש להוסיף לפחות המחאה אחת');
          setLoading(false);
          return false;
        }

        const normalized = checksToSend.map((c) => ({
          referenceId: (c.referenceId ?? '').trim(),
          amount: typeof c.amount === 'number' ? c.amount : NaN,
          bankNumber: (c.bankNumber ?? '').trim(),
          branchNumber: (c.branchNumber ?? '').trim(),
          accountNumber: (c.accountNumber ?? '').trim(),
          dueDate: c.dueDate,
          allocations: (c.allocations ?? []).map((a) => ({
            allocationId: a.allocationId,
            amount: typeof a.amount === 'number' ? a.amount : NaN,
          })),
        }));
        const invalid = normalized.find(
          (c) =>
            !c.referenceId ||
            !Number.isFinite(c.amount) ||
            c.amount <= 0 ||
            !c.bankNumber ||
            !c.branchNumber ||
            !c.accountNumber ||
            !c.allocations ||
            c.allocations.length === 0 ||
            c.allocations.some(
              (a) =>
                !Number.isFinite(a.amount) || a.amount <= 0 || !a.allocationId,
            ),
        );
        if (invalid) {
          toast.warn('יש להשלים את פרטי כל ההמחאות ופיצול השירותים');
          setLoading(false);
          return false;
        }
        const totalChecksAmount = normalized.reduce(
          (sum, c) => sum + (c.amount || 0),
          0,
        );
        const totalChecksAmountRounded = round2(totalChecksAmount);
        if (totalChecksAmountRounded !== totalPaymentAmount) {
          toast.warn(
            `סכום ההמחאות חייב להיות זהה לסכום התשלום הכולל (₪${totalPaymentAmount.toLocaleString('he-IL')})`,
          );
          setLoading(false);
          return false;
        }

        // Validate per-check allocations sum equals check amount (rounded)
        const invalidCheckSplit = normalized.find((c) => {
          const sumAlloc = round2(
            (c.allocations ?? []).reduce((s, a) => s + (a.amount || 0), 0),
          );
          return sumAlloc !== round2(c.amount);
        });
        if (invalidCheckSplit) {
          toast.warn('סכום הפיצול בכל המחאה חייב להיות זהה לסכום ההמחאה');
          setLoading(false);
          return false;
        }

        // Validate per-allocation totals across all checks equals allocationAmounts (rounded)
        const totalByAllocationId: Record<number, number> = {};
        for (const c of normalized) {
          for (const a of c.allocations) {
            totalByAllocationId[a.allocationId] = round2(
              (totalByAllocationId[a.allocationId] || 0) + (a.amount || 0),
            );
          }
        }
        const invalidAllocationTotal = allocationAmounts.find((alloc) => {
          const expected = round2(alloc.amount || 0);
          const got = round2(totalByAllocationId[alloc.allocationId] || 0);
          return expected !== got;
        });
        if (invalidAllocationTotal) {
          toast.warn('פיצול ההמחאות חייב לכסות את כל שירותי התשלום בדיוק');
          setLoading(false);
          return false;
        }

        const checkAllocationRes = await appApi.createPaymentAllocation(
          user.id,
          {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'check',
            paymentDetails: {
              checks: normalized.map((c) => ({
                amount: c.amount,
                referenceId: c.referenceId,
                bankNumber: c.bankNumber,
                branchNumber: c.branchNumber,
                accountNumber: c.accountNumber,
                ...(c.dueDate && {
                  dueDate: dateUtils.formattedDateWithNoTime(c.dueDate),
                }),
                allocations: c.allocations.map((a) => ({
                  allocationId: a.allocationId,
                  amount: a.amount,
                })),
              })),
            },
            ...receiptFields,
          },
        );
        if (checkAllocationRes.data.errCode) {
          toast.warn(displayErrorText(checkAllocationRes.data.errCode));
          setLoading(false);
          return false;
        }
        break;
      }

      case PaymentMethod.Scholarship:
        if (
          !scholarshipData.scholarshipId ||
          !scholarshipData.studentScholarshipId
        ) {
          toast.warn('יש לבחור מלגה תלמיד תקפה');
          setLoading(false);
          return false;
        }
        const scholarshipAllocationRes = await appApi.createPaymentAllocation(
          user.id,
          {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'scholarship',
            paymentDetails: {
              scholarshipId: scholarshipData.scholarshipId,
              studentScholarshipId: scholarshipData.studentScholarshipId,
            },
            ...receiptFields,
          },
        );
        if (scholarshipAllocationRes.data.errCode) {
          toast.warn(displayErrorText(scholarshipAllocationRes.data.errCode));
          setLoading(false);
          return false;
        }
        break;

      case PaymentMethod.Cash:
        const cashAllocationRes = await appApi.createPaymentAllocation(
          user.id,
          {
            allocations: allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
            paymentMethod: 'cash',
            ...receiptFields,
          },
        );
        if (cashAllocationRes.data.errCode) {
          toast.warn(displayErrorText(cashAllocationRes.data.errCode));
          setLoading(false);
          return false;
        }
        break;

      case PaymentMethod.CreditCardInstallments:
        if (
          !installmentsData.numberOfInstallments ||
          installmentsData.numberOfInstallments < 1 ||
          installmentsData.numberOfInstallments > 12
        ) {
          toast.warn('יש לבחור מספר תשלומים בין 1 ל-12');
          setLoading(false);
          return false;
        }
        if (!installmentsData.selectedCreditCardTokenId) {
          toast.warn('יש לבחור כרטיס אשראי');
          setLoading(false);
          return false;
        }
        const firstPaymentDate = calculateFirstPaymentDate();
        await appApi.createPaymentInstallments(user.id, {
          allocations: allocationAmounts.map((alloc) => ({
            allocationId: alloc.allocationId,
            amount: alloc.amount,
          })),
          numberOfInstallments: installmentsData.numberOfInstallments,
          firstPaymentDate: firstPaymentDate.toISOString(),
          paymentDayOfMonth: 20,
          creditCardTokenId: installmentsData.selectedCreditCardTokenId,
        });
        break;

      case PaymentMethod.BankTransferInstallments:
        if (
          !bankTransferInstallmentsData.numberOfInstallments ||
          bankTransferInstallmentsData.numberOfInstallments < 1 ||
          bankTransferInstallmentsData.numberOfInstallments > 12
        ) {
          toast.warn('יש לבחור מספר תשלומים בין 1 ל-12');
          setLoading(false);
          return false;
        }
        if (
          !bankTransferInstallmentsData.bankNumber ||
          !bankTransferInstallmentsData.branchNumber ||
          !bankTransferInstallmentsData.accountNumber
        ) {
          toast.warn('יש למלא את כל פרטי חשבון הבנק');
          setLoading(false);
          return false;
        }
        // Calculate bank transfer first payment date based on user selection
        const bankDefaultMonth = calculateDefaultFirstPaymentMonth();
        const bankFirstPaymentDate =
          bankTransferInstallmentsData.firstPaymentMonth
            ? new Date(
                bankTransferInstallmentsData.firstPaymentMonth.year,
                bankTransferInstallmentsData.firstPaymentMonth.month,
                20,
              )
            : new Date(bankDefaultMonth.year, bankDefaultMonth.month, 20);
        await appApi.createBankTransferInstallments(user.id, {
          allocations: allocationAmounts.map((alloc) => ({
            allocationId: alloc.allocationId,
            amount: alloc.amount,
          })),
          numberOfInstallments:
            bankTransferInstallmentsData.numberOfInstallments,
          firstPaymentDate: bankFirstPaymentDate.toISOString(),
          paymentDayOfMonth: 20,
          bankNumber: bankTransferInstallmentsData.bankNumber,
          branchNumber: bankTransferInstallmentsData.branchNumber,
          accountNumber: bankTransferInstallmentsData.accountNumber,
        });
        break;

      case PaymentMethod.ExistingCreditCardInstallments:
        if (!selectedExistingInstallmentPlanId) {
          toast.warn('יש לבחור הוראת קבע קיימת');
          setLoading(false);
          return false;
        }
        const existingCreditCardInstallmentRes =
          await appApi.addToExistingInstallmentPlan(
            selectedExistingInstallmentPlanId,
            user.id,
            allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
          );
        if (existingCreditCardInstallmentRes.data.errCode) {
          toast.warn(
            displayErrorText(existingCreditCardInstallmentRes.data.errCode),
          );
          setLoading(false);
          return false;
        } else {
          toast.success('שירותי התשלום נוספו להוראת הקבע בהצלחה');
        }

        break;

      case PaymentMethod.ExistingBankTransferInstallments:
        if (!selectedExistingBankTransferPlanId) {
          toast.warn('יש לבחור הוראת קבע קיימת');
          setLoading(false);
          return false;
        }
        const existingInstallmentBankTransferRes =
          await appApi.addToExistingInstallmentPlanBankTransfer(
            selectedExistingBankTransferPlanId,
            user.id,
            allocationAmounts.map((alloc) => ({
              allocationId: alloc.allocationId,
              amount: alloc.amount,
            })),
          );

        if (existingInstallmentBankTransferRes.data.errCode) {
          toast.warn(
            displayErrorText(existingInstallmentBankTransferRes.data.errCode),
          );
          setLoading(false);
          return false;
        } else {
          toast.success('שירותי התשלום נוספו להוראת הקבע בהצלחה');
        }

        break;
    }
    // Refresh user details to get updated payment services
    const updatedUserDetails = await appApi.getUserStudentDetails(user.id);
    globalUserPage.studentDetails = updatedUserDetails.data.body;
    setLoading(false);
    onHide();
    //setLoading(false);
    return true;
  } catch (error) {
    console.error('Error processing payment:', error);
    setLoading(false);
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים...');
    // Show error message
    return false;
  }
};
